import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | HTMLButtonElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  restoreFocusOnClose?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
}

function Modal({
                 isOpen,
                 onClose,
                 title = "Dialog",
                 children,
                 initialFocusRef,
                 returnFocusRef,
                 closeOnBackdrop = true,
                 closeOnEsc = true,
                 restoreFocusOnClose = true,
                 size = "md" // sm | md | lg | xl | 2xl
               }: ModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const closeModal = () => {
    setIsVisible(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      setShouldRender(false);
      onClose();
    }, 300);
  };

  // Animation and scroll lock
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const { overflow } = document.body.style;
      document.body.style.overflow = "hidden";
      // Trigger animation after state update
      setTimeout(() => setIsVisible(true), 10);
      return () => {
        document.body.style.overflow = overflow;
      };
    } else if (shouldRender) {
      // Only trigger close animation if modal is currently rendered
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [isOpen, shouldRender]);

  // Focus management
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusableSelectors = [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ];
    const focusables = panel.querySelectorAll(focusableSelectors.join(","));
    const firstFocusable = initialFocusRef?.current || (focusables[0] as HTMLElement);

    const prevActive = document.activeElement;
    const returnElement = returnFocusRef?.current;

    if (firstFocusable && 'focus' in firstFocusable) {
      (firstFocusable as HTMLElement).focus();
    } else {
      panel.focus();
    }

    return () => {
      if (!restoreFocusOnClose) {
        return;
      }
      const toRestore = returnElement || prevActive;
      if (toRestore && toRestore instanceof HTMLElement) {
        toRestore.focus();
      }
    };
  }, [isOpen, initialFocusRef, returnFocusRef, restoreFocusOnClose]);

  // Handle ESC & TAB trap
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && closeOnEsc) {
      e.stopPropagation();
      closeModal();
      return;
    }
    if (e.key === "Tab") {
      const panel = panelRef.current;
      if (!panel) return;

      const focusableSelectors = [
        'a[href]:not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])',
      ];
      const nodes = Array.from(panel.querySelectorAll(focusableSelectors.join(",")));

      if (nodes.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        (first as HTMLElement).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        (last as HTMLElement).focus();
      }
    }
  };

  // Backdrop click
  const onBackdropMouseDown = (e: React.MouseEvent) => {
    if (!closeOnBackdrop) return;
    if (e.target === backdropRef.current) {
      closeModal();
    }
  };

  if (!shouldRender) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
  };

  return createPortal(
    <div
      ref={backdropRef}
      onMouseDown={onBackdropMouseDown}
      onKeyDown={onKeyDown}
      className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        tabIndex={-1}
        className={`bg-white rounded-2xl shadow-xl w-full ${sizeClasses[size]} max-h-[100vh] overflow-auto focus:outline-none transition-all duration-300 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {title}
          </h2>
          <button
            onClick={closeModal}
            aria-label="Close dialog"
            className="text-2xl leading-none hover:text-gray-600"
          >
            ×
          </button>
        </div>
        <div id="modal-desc" className="px-4 py-3 text-gray-700">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default Modal;
